<template>
  <page-main
    title="Organizations & Certifications"
    nextText="Education"
    buttonTabIndex="4"
    eventName="nav"
    @nav="nav('/Education')"
    @save="save()"
  >
    <template v-slot:aside>
      Being involved in organizations (professional or volunteer) shows your
      involvement in your profession and community. List organizations you
      actively belong to or belonged to in the past.
    </template>
    <template v-slot:nav>
      <v-system-bar color="accent" height="5" class="mt-4"></v-system-bar>
      <modal cardTitle="Example" ctaText="Need an example?">
        <ul class="font-italic">
          <li class="mb-4">
            <span class="font-weight-bold">Example #1:</span>
            <ul>
              <li>
                <span class="font-weight-bold"
                  >Professional organizations you belong to:</span
                >
                American Society of Mechanical Engineers (ASME) (2013-present)
              </li>
              <li>
                <span class="font-weight-bold"
                  >Volunteer organizations you belong to:</span
                >
                Rotaract (2015-2018)
              </li>
              <li>
                <span class="font-weight-bold">Licenses & Certifications:</span>
                Six Sigma Greenbelt (2014)
              </li>
            </ul>
          </li>
          <li class="mb-4">
            <span class="font-weight-bold">Example #2:</span>
            <ul>
              <li>
                <span class="font-weight-bold"
                  >Professional Organizations you are a member of:</span
                >
                American Society of Mechanical Engineers (ASME) (2013-present)
              </li>
              <li>
                <span class="font-weight-bold"
                  >Volunteer Organizations you are part of:</span
                >
                Rotaract (2015-2018)
              </li>
              <li>
                <span class="font-weight-bold">Licenses & Certifications:</span>
                Fundamentals of Engineering (2014-2019)
              </li>
            </ul>
          </li>
        </ul>
      </modal>
      <modal cardTitle="Tips" ctaText="Need a few tips?" :accentButton="true">
        <r-tip-container :tips="tips"></r-tip-container>
      </modal>
    </template>
    <v-container class="fade-in">
      <v-row>
        <v-col cols="12" class="r-col">
          <r-multi-select
            label="Add professional organizations here one at a time"
            titleText="Professional organizations you are a member of:"
            :optionsList="[]"
            :selected.sync="professionalOrg"
            tabindex="1"
            autofocus
            :maxSelections="25"
          ></r-multi-select>
        </v-col>
        <v-col cols="12" class="r-col">
          <r-multi-select
            label="Add volunteer organizations here one at a time"
            titleText="Volunteer organizations you are a part of:"
            :optionsList="[]"
            :selected.sync="volunteerOrg"
            tabindex="2"
            :maxSelections="25"
          ></r-multi-select>
        </v-col>
        <v-col v-if="licensesCerts.length > 0" cols="12" class="r-col">
          <v-alert type="info" text dense class="mb-3 r-card">
            <div class="d-flex align-center justify-space-between">
              <div>
                <strong>{{ licensesCerts.length }} legacy credential{{ licensesCerts.length !== 1 ? 's' : '' }}</strong> can be migrated to Structured Credentials for richer detail (expiration tracking, jurisdiction, etc.).
              </div>
              <v-btn small color="primary" class="ml-3" @click="migrateLegacyCredentials" :loading="isMigrating">
                <v-icon left small>mdi-transfer-right</v-icon>
                Migrate All
              </v-btn>
            </div>
          </v-alert>
          <r-multi-select
            label="Add licenses & certifications here one at a time"
            titleText="Licenses & Certifications (Legacy):"
            :optionsList="[]"
            :selected.sync="licensesCerts"
            tabindex="3"
            :maxSelections="25"
          ></r-multi-select>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-divider class="my-2"></v-divider>
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="text-subtitle-1 font-weight-bold">Credentials</span>
            <v-btn color="primary" small @click="openCredentialDialog()">
              <v-icon left small>mdi-plus</v-icon> Add Credential
            </v-btn>
          </div>
          <div v-if="credentials.length === 0 && licensesCerts.length === 0" class="empty-state">
            <v-icon class="empty-state-icon">mdi-certificate-outline</v-icon>
            <div class="empty-state-title">No credentials yet</div>
            <div class="empty-state-subtitle">Add your certifications, licenses, and professional credentials.</div>
          </div>
          <v-card v-for="(cred, idx) in credentials" :key="idx" outlined class="mb-2 pa-3 r-card">
            <div class="d-flex align-center justify-space-between">
              <div>
                <span class="font-weight-bold">{{ cred.name }}</span>
                <v-chip v-if="cred.credentialType" x-small class="ml-2" color="secondary" outlined>{{ cred.credentialType }}</v-chip>
                <v-chip v-if="cred.status" x-small class="ml-1" :color="cred.status === 'Active' ? 'success' : cred.status === 'Expired' ? 'error' : 'warning'" outlined>{{ cred.status }}</v-chip>
              </div>
              <div>
                <v-btn icon x-small @click="openCredentialDialog(idx)"><v-icon small>mdi-pencil</v-icon></v-btn>
                <v-btn icon x-small @click="removeCredential(idx)"><v-icon small color="error">mdi-delete</v-icon></v-btn>
              </div>
            </div>
            <div v-if="cred.issuingAuthority" class="text-caption grey--text">{{ cred.issuingAuthority }}</div>
            <div v-if="cred.jurisdiction" class="text-caption grey--text">{{ cred.jurisdictionType ? cred.jurisdictionType + ': ' : '' }}{{ cred.jurisdiction }}</div>
            <div v-if="cred.associatedSkills && cred.associatedSkills.length" class="mt-1">
              <v-chip v-for="(skill, si) in cred.associatedSkills" :key="si" x-small class="mr-1" color="accent" outlined>{{ skill }}</v-chip>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog v-model="credentialDialog" max-width="650" persistent>
      <v-card class="r-card">
        <v-card-title>{{ editingCredentialIndex !== null ? 'Edit' : 'Add' }} Credential</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12">
              <v-text-field v-model="credentialForm.name" label="Credential Name *" dense outlined placeholder="e.g. Six Sigma Green Belt, PMP, AWS Solutions Architect"></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-select v-model="credentialForm.credentialType" :items="credentialTypes" label="Type" dense outlined placeholder="Certification, License, etc."></v-select>
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="credentialForm.issuingAuthority" label="Issuing Authority" dense outlined placeholder="e.g. PMI, AWS, State Board"></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="credentialForm.jurisdiction" label="Jurisdiction" dense outlined placeholder="e.g. Nebraska, California, USA" hint="Where this credential is recognized"></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-select v-model="credentialForm.jurisdictionType" :items="jurisdictionTypes" label="Jurisdiction Scope" dense outlined></v-select>
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="credentialForm.issueDate" label="Issue Date" dense outlined type="date"></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="credentialForm.expirationDate" label="Expiration Date" dense outlined type="date" :disabled="credentialForm.doesNotExpire"></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-checkbox v-model="credentialForm.doesNotExpire" label="Does Not Expire" dense></v-checkbox>
            </v-col>
            <v-col cols="6">
              <v-select v-model="credentialForm.status" :items="credentialStatuses" label="Status" dense outlined></v-select>
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="credentialForm.credentialId" label="Credential ID" dense outlined placeholder="e.g. CERT-12345"></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="credentialForm.credentialUrl" label="Verification URL" dense outlined placeholder="e.g. https://verify.example.com/cert/12345"></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-combobox v-model="credentialForm.associatedSkills" label="Associated Skills" dense outlined multiple small-chips deletable-chips placeholder="e.g. Project Management, Cloud Architecture" hint="Type a skill and press Enter to add it"></v-combobox>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="credentialDialog = false">Cancel</v-btn>
          <v-btn color="primary" :disabled="!credentialForm.name" @click="saveCredentialForm">{{ editingCredentialIndex !== null ? 'Update' : 'Add' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </page-main>
</template>

<script>
import * as Display from "../utility/display";
import { mapFields } from "vuex-map-fields";
import { mapActions, mapState } from "vuex";
import Modal from "@/components/Modal.vue";

function emptyCredentialForm() {
  return {
    name: "",
    credentialType: "",
    issuingAuthority: "",
    jurisdiction: "",
    jurisdictionType: "",
    issueDate: "",
    expirationDate: "",
    doesNotExpire: false,
    credentialId: "",
    credentialUrl: "",
    status: "Active",
    associatedSkills: []
  };
}

export default {
  name: "Organizations",
  data: () => ({
    credentialDialog: false,
    editingCredentialIndex: null,
    credentialForm: emptyCredentialForm(),
    credentialTypes: ["License", "Certification", "Registration", "Permit", "Accreditation", "Other"],
    jurisdictionTypes: ["State", "Federal", "National", "International", "Province", "Regional"],
    credentialStatuses: ["Active", "Inactive", "Expired", "Pending", "Revoked"],
    isMigrating: false
  }),
  components: { Modal },
  methods: {
    save() {
      this.buildDisplay();
      this.saveOrganizations();
      this.saveLicenseCert();
      if (this.credentials.length > 0) {
        this.saveCredentials();
      }
    },
    ...mapActions("organization", ["saveOrganizations"]),
    ...mapActions("licenseCert", ["saveLicenseCert", "saveCredentials"]),
    nav(routerPath) {
      this.$router.push({ path: routerPath });
    },
    buildDisplay() {
      let professionalOrgs = Display.BuildCommaString("", this.professionalOrg);
      let volunteerOrgs = Display.BuildCommaString("", this.volunteerOrg);
      let licensesDisplay = Display.BuildCommaString("", this.licensesCerts);

      this.professionalOrgDisplay = professionalOrgs;
      this.volunteerOrgDisplay = volunteerOrgs;
      this.licensesCertsDisplay = licensesDisplay;

      return [professionalOrgs, volunteerOrgs, licensesDisplay];
    },
    openCredentialDialog(index) {
      if (index !== undefined && index !== null) {
        this.editingCredentialIndex = index;
        this.credentialForm = JSON.parse(JSON.stringify(this.credentials[index]));
      } else {
        this.editingCredentialIndex = null;
        this.credentialForm = emptyCredentialForm();
      }
      this.credentialDialog = true;
    },
    saveCredentialForm() {
      if (!this.credentialForm.name) return;
      if (this.credentialForm.doesNotExpire) {
        this.credentialForm.expirationDate = "";
      }
      let updated = [...this.credentials];
      if (this.editingCredentialIndex !== null) {
        updated.splice(this.editingCredentialIndex, 1, { ...this.credentialForm });
      } else {
        updated.push({ ...this.credentialForm });
      }
      this.$store.commit('licenseCert/setCredentials', updated);
      this.credentialDialog = false;
    },
    removeCredential(index) {
      let updated = [...this.credentials];
      updated.splice(index, 1);
      this.$store.commit('licenseCert/setCredentials', updated);
    },
    async migrateLegacyCredentials() {
      if (!this.licensesCerts || this.licensesCerts.length === 0) return;
      this.isMigrating = true;
      try {
        var existingNames = new Set((this.credentials || []).map(function(c) { return (c.name || '').toLowerCase().trim(); }));
        var newCreds = [];
        for (var i = 0; i < this.licensesCerts.length; i++) {
          var item = this.licensesCerts[i];
          var name = (typeof item === 'string' ? item : (item.text || item.name || '')).trim();
          if (!name || existingNames.has(name.toLowerCase())) continue;
          existingNames.add(name.toLowerCase());
          newCreds.push({
            name: name,
            credentialType: "Certification",
            issuingAuthority: "",
            jurisdiction: "",
            jurisdictionType: "",
            issueDate: "",
            expirationDate: "",
            doesNotExpire: false,
            credentialId: "",
            credentialUrl: "",
            status: "Active",
            associatedSkills: []
          });
        }
        var merged = (this.credentials || []).concat(newCreds);
        this.$store.commit('licenseCert/setCredentials', merged);
        await this.saveCredentials(merged);
        this.licensesCerts = [];
        this.licensesCertsDisplay = "";
        await this.saveLicenseCert();
      } finally {
        this.isMigrating = false;
      }
    }
  },
  mounted() {},
  computed: {
    displayValues: function () {
      return this.buildDisplay();
    },
    ...mapFields("organization", [
      "professionalOrg",
      "professionalOrgDisplay",
      "volunteerOrg",
      "volunteerOrgDisplay",
    ]),
    ...mapFields("licenseCert", ["licensesCerts", "licensesCertsDisplay"]),
    ...mapState("licenseCert", ["credentials"]),
    tips() {
      return [
        "Where applicable, we recommend listing the year(s) you were active in parenthesis after the name of the organization. If you have a license or certification, we recommend you put the year you earned it and the year it expires if applicable.",
      ];
    },
  },
};
</script>
